import { CSSProperties, FC, useState } from 'react';
import classNames from 'classnames';

import { Option } from '@gn-audio/jss-core/common/models';

import styles from './ColorFilter.module.scss';

export interface ColorFilterProps {
  options: Option[];
  selected?: Option | Option[];
  onSelect?: (option: Option) => void;
  dotSize?: number;
  showColorTitle?: boolean;
  containerClassName?: string;
}

const getDotBackgroundStyles = (colorString: string): CSSProperties => {
  const [firstColor, secondColor] = colorString.split('|');
  if (!secondColor) return { backgroundColor: `#${firstColor}` };
  const gradient = `linear-gradient(45deg, #${firstColor} 0%, #${firstColor} 50%, #${secondColor} 50%, #${secondColor} 100%)`;
  return { backgroundImage: gradient };
};

export const ColorFilter: FC<ColorFilterProps> = ({
  options,
  selected,
  onSelect,
  dotSize = 24,
  showColorTitle = true,
  containerClassName,
}) => {
  const [hoverColor, setHoverColor] = useState<Option | undefined>();
  const isMultiplePicker = Array.isArray(selected);
  const buttonSize = dotSize + 12;

  const handleSelect = (option: Option) => {
    onSelect && onSelect(option);
  };

  const handleOnFocus = (option: Option) => {
    !isMultiplePicker && handleSelect(option);
  };

  const handleFocusColor = (option?: Option) => {
    setHoverColor(option);
  };

  return (
    <>
      <ul
        className={classNames('flex gap-3', containerClassName)}
        role={isMultiplePicker ? 'list' : 'tablist'}
      >
        {options.map((option) => {
          const dotStyles = getDotBackgroundStyles(option.value);
          let isOptionChecked: boolean;

          if (isMultiplePicker) {
            isOptionChecked = selected.some((color) => color.value === option.value);
          } else {
            isOptionChecked = selected?.value === option.value;
          }
          return (
            <li key={option.value} role={isMultiplePicker ? 'listitem' : 'presentation'}>
              <button
                disabled={option.disabled}
                style={{ width: buttonSize, height: buttonSize }}
                className={classNames(styles.colorCircle, isOptionChecked && styles.selected)}
                role={isMultiplePicker ? undefined : 'tab'}
                title={option.title}
                aria-label={option.title}
                aria-pressed={isMultiplePicker ? isOptionChecked : undefined}
                aria-selected={isMultiplePicker ? undefined : isOptionChecked}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => handleFocusColor(option)}
                onMouseLeave={() => handleFocusColor(undefined)}
                onFocus={() => handleOnFocus(option)}
              >
                <div style={{ ...dotStyles, width: dotSize, height: dotSize }}></div>
              </button>
            </li>
          );
        })}
      </ul>
      {showColorTitle && !isMultiplePicker && (
        <p className="mb-4 mt-2 text-[0.75rem] text-gray-700">
          {hoverColor?.title ?? selected?.title}
        </p>
      )}
    </>
  );
};
